import React from 'react';
import {
  DecoratorNode,
  NodeKey,
  Spread,
  SerializedLexicalNode,
  EditorConfig,
  createCommand,
  LexicalCommand,
  DOMConversionMap,
  DOMConversionOutput,
  LexicalEditor,
  DOMExportOutput,
} from 'lexical';
import { Image } from 'semantic-ui-react';

export type ImagePayload = {
  src: string;
  altText: string;
  maxWidth?: number;
  width?: number | 'inherit';
  height?: number | 'inherit';
  name?: string;
};

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src } = domNode;
    const node = $createImageNode({ altText, src });
    return { node };
  }
  return null;
}

export type SerializedImageNode = Spread<
  {
    type: 'image';
    src: string;
    altText: string;
    maxWidth: number;
    width?: number | 'inherit';
    height?: number | 'inherit';
    name?: string;
    version: 1;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<React.ReactElement> {
  __src: string;
  __altText: string;
  __maxWidth: number;
  __width?: 'inherit' | number;
  __height?: 'inherit' | number;
  __name?: string;

  constructor(
    src: string,
    altText: string,
    maxWidth: number,
    width?: number | 'inherit',
    height?: number | 'inherit',
    name?: string,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__maxWidth = maxWidth;
    this.__width = width;
    this.__height = height;
    this.__name = name;
  }

  getAltText(): string {
    return this.__altText;
  }

  setAltText(alt: string): void {
    this.__altText = alt;
  }

  getSrc(): string {
    return this.__src;
  }

  setSrc(src: string): void {
    const self = this.getWritable();
    self.__src = src;
  }

  getName(): string | undefined {
    return this.__name;
  }

  setName(name: string) {
    const self = this.getWritable();
    self.__name = name;
  }

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__name,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    return $createImageNode({
      src: serializedNode.src,
      altText: serializedNode.altText,
      maxWidth: serializedNode.maxWidth,
      width: serializedNode.width,
      height: serializedNode.height,
    });
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.setAttribute('class', 'ui image rounded');
    return { element };
  }

  exportJSON(): SerializedImageNode {
    return {
      type: 'image',
      src: this.getSrc(),
      altText: this.getAltText(),
      maxWidth: this.__maxWidth,
      version: 1,
      width: this.__width === 'inherit' ? 0 : this.__width,
      height: this.__height === 'inherit' ? 0 : this.__height,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(
    _prevNode: unknown,
    _dom: HTMLElement,
    _config: EditorConfig,
  ): boolean {
    return false;
  }

  decorate(): React.ReactElement {
    return (
      <Image
        bordered
        rounded
        src={this.__src}
        alt={this.__altText}
        key={this.getKey()}
      />
    );
  }
}

export const $createImageNode = ({
  src,
  altText,
  maxWidth = 350,
  width,
  height,
  name,
}: ImagePayload): ImageNode => {
  return new ImageNode(src, altText, maxWidth, width, height, name);
};

export const $isImageNode = (node: any): node is ImageNode => {
  return node instanceof ImageNode;
};

export const INSERT_IMAGE_COMMAND: LexicalCommand<ImagePayload> =
  createCommand();

export const INSERT_LOADING_IMAGE_COMMAND: LexicalCommand<
  Partial<ImagePayload>
> = createCommand();
